import React, { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Alert, DatePicker, Empty, Flex, Pagination, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useStyle } from 'segments/hooks';

import { PagintaionParams } from 'shared/CustomPagination/CustomPagination';

import { getActiveWaitersList, getActiveWaitingTimes, useGetWaitingListQuery } from 'features/bookingWait';
import { selectWorkTime } from 'features/config';
import { useGetCourtsListQuery } from 'features/courts';
import { convertCourtsBookingsToTable, CourtsBooking, SelectedBookingCourt, useGetCourtsBookingQuery } from 'features/courtsBooking';

import BookingTableColumns from './BookingTableColumns/BookingTableColumns';


const todayDate = dayjs();
interface Props {
  handleBooking: (arg: SelectedBookingCourt) => void;
  handleCheckDetail: (ids: number[]) => void;
  createWaitingList: (time: string) => void;
  openWaitersList: (time: string) => void;
  editWaiting: (bookingWaitsId: number[]) => void;
}

type CourtBooking = CourtsBooking[number];
export interface TableData extends CourtBooking {
  key: number;
}

export function CourtsBookings({ handleBooking, handleCheckDetail, createWaitingList, editWaiting, openWaitersList }: Props) {
  const { t } = useTranslation();
  const [bookingTime, setBookingTime] = useState(todayDate);
  const [params, setParams] = useState<PagintaionParams>();
  const workTime = useAppSelector(selectWorkTime);
  const { styles } = useStyle();
  const { data: bookingWaitList = [], isLoading: isWaitingLoading } = useGetWaitingListQuery(bookingTime.format('YYYY-MM-DD'));
  const { data = [], isLoading, isError } = useGetCourtsBookingQuery({
    dateFrom: bookingTime.format('YYYY-MM-DD'),
    dateTo: bookingTime.format('YYYY-MM-DD')
  });
  const { data: courts = [] } = useGetCourtsListQuery();
  const columnCount = data.length === 0 ? null : data[0].columns.length;

  useEffect(() => {
    if (columnCount !== null) {
      setParams({
        itemsCount: columnCount,
        pageSize: columnCount,
        pageNumber: 1,
      });
    }
  }, [setParams, columnCount]);


  if (isLoading || isWaitingLoading) {
    return <LoadingOutlined />;
  }

  if (isError || data.length === 0 || columnCount === 0) {
    return (
      <>
        <DatePicker value={bookingTime} onChange={(value) => setBookingTime(value ?? todayDate)} />
        {isError ? <Alert type="error" showIcon message={t('admin-extensions.load-error')} /> : <Empty />}
      </>
    );
  }

  if (params?.itemsCount !== columnCount || params === undefined) {
    return <LoadingOutlined />;
  }

  const activeTimeList = getActiveWaitingTimes(bookingWaitList);
  const activeWaitingUsersList = getActiveWaitersList(bookingWaitList);
  const handleTimeChange = (time: Dayjs | null) => setBookingTime(time ?? todayDate);
  const onTimeClick = (time: string) => {
    if (activeWaitingUsersList.length === 0) {
      createWaitingList(time);
    } else {
      openWaitersList(time);
    }
  };

  const columns = BookingTableColumns({
    items: data,
    handleCheckDetail,
    handleBooking,
    onTimeChange: handleTimeChange,
    editWaiting,
    onTimeClick,
    bookingTime,
    params,
    courts,
    workTime,
    activeTimeList,
    waitingList: bookingWaitList,
  });

  const handleChange = (page: number, size: number) => {
    setParams({
      ...params,
      pageNumber: page,
      pageSize: size,
    });
  };

  return (
    <Table<TableData>
      className={styles.customTable}
      size="small"
      dataSource={convertCourtsBookingsToTable(data)}
      loading={isLoading}
      scroll={{ x: 1200, y: '80vh' }}
      bordered
      pagination={false}
      columns={columns}
      footer={() => (
        <Flex justify='center'>
          <Pagination total={params.itemsCount} pageSize={params.pageSize} pageSizeOptions={[5, 10, 15, 20, columnCount]} onChange={handleChange} showSizeChanger />
        </Flex>
      )}
    />
  );
}
